import axios from 'axios'

import { apiUrl } from '@/../app.config'

import { setupFeature } from './interceptors'

export { apiUrl }
export const apiPort = new URL(apiUrl).port || null

/**
 * Request manager to handle active requests and cancellation
 */
export class RequestManager {
  constructor() {
    this.requestTokenSources = []
  }

  addRequestTokenSource(source) {
    this.requestTokenSources.push(source)
  }

  removeRequestTokenSource(source) {
    const index = this.requestTokenSources.indexOf(source)
    if (index !== -1) {
      this.requestTokenSources.splice(index, 1)
    }
  }

  cancelAllRequests(reason = 'Request cancelled by user') {
    this.requestTokenSources.forEach((source) => {
      source.cancel(reason)
    })
    this.requestTokenSources = []
  }

  getActiveRequestCount() {
    return this.requestTokenSources.length
  }
}

// Create a single instance of the request manager
export const requestManager = new RequestManager()

/**
 * Default configuration for all API clients
 */
const DEFAULT_CONFIG = {
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
}

/**
 * ApiClient factory for creating customized API instances
 */
export class ApiClient {
  /**
   * Creates a new API client instance with specified features
   * @param {Object} options - Configuration options
   * @returns {AxiosInstance} Configured axios instance
   */
  static create(options = {}) {
    // Merge configs using native JavaScript object spreading
    const config = {
      ...DEFAULT_CONFIG,
      ...(options.axiosConfig || {}),
      headers: {
        ...DEFAULT_CONFIG.headers,
        ...(options.axiosConfig?.headers || {}),
      },
    }

    // All features off by default - services must explicitly enable what they need
    const features = {
      auth: false,
      urlNormalise: false,
      cancellation: false,
      connectionHandling: false,
      timeoutHandling: false,
      blobHandling: false,
      ...(options.features || {}),
    }

    const instance = axios.create(config)
    // Apply each feature individually in a specific order
    if (features.urlNormalise) setupFeature.urlNormalise(instance, apiUrl)
    if (features.auth) setupFeature.auth(instance)
    if (features.connectionHandling) setupFeature.connection(instance)
    if (features.timeoutHandling) setupFeature.timeout(instance)
    if (features.cancellation)
      setupFeature.cancellation(instance, requestManager)
    if (features.blobHandling) setupFeature.blob(instance)

    return instance
  }
}

// Create service instances - each explicitly enables only what it needs
const noAuthService = ApiClient.create({
  features: {
    urlNormalise: true,
    connectionHandling: true,
    timeoutHandling: true,
  },
})

const axiosService = ApiClient.create({
  features: {
    auth: true,
    urlNormalise: true,
    cancellation: true,
    connectionHandling: true,
    timeoutHandling: true,
  },
})

const galaxyService = ApiClient.create({
  features: {
    cancellation: true,
    timeoutHandling: true,
  },
})

const axiosBlobJsonService = ApiClient.create({
  axiosConfig: {
    headers: { Accept: undefined },
    responseType: 'blob',
  },
  features: {
    auth: true,
    urlNormalise: true,
    cancellation: true,
    connectionHandling: true,
    timeoutHandling: true,
    blobHandling: true,
  },
})

// Clean modern API with standard axios signature (separate data and options)
export const http = {
  get: (url, opts = {}) => axiosService({ method: 'get', url, ...opts }),

  post: (url, data, opts = {}) => {
    // remove Content-Type to let axios set multipart/form-data automatically
    if (data instanceof FormData) {
      opts = {
        ...opts,
        headers: {
          ...opts.headers,
          'Content-Type': undefined,
        },
      }
    }
    return axiosService({ method: 'post', url, data, ...opts })
  },

  put: (url, data, opts = {}) =>
    axiosService({ method: 'put', url, data, ...opts }),

  patch: (url, data, opts = {}) =>
    axiosService({ method: 'patch', url, data, ...opts }),

  delete: (url, opts = {}) => axiosService({ method: 'delete', url, ...opts }),

  noAuth: {
    get: (url, opts = {}) => noAuthService({ method: 'get', url, ...opts }),

    post: (url, data, opts = {}) =>
      noAuthService({ method: 'post', url, data, ...opts }),
  },

  galaxy: {
    get: (url, opts = {}) => galaxyService({ method: 'get', url, ...opts }),
  },

  blob: {
    get: (url, opts = {}) =>
      axiosBlobJsonService({ method: 'get', url, ...opts }),

    put: (url, data, opts = {}) =>
      axiosBlobJsonService({ method: 'put', url, data, ...opts }),
  },

  requests: {
    cancelAll: (reason) => requestManager.cancelAllRequests(reason),
    getActiveCount: () => requestManager.getActiveRequestCount(),
  },
}

// ===== RETROCOMPATIBILITY LAYER =====
// Here we handle the legacy pattern where data is passed inside the options object

/**
 * Extract data from legacy options format
 * @param {Object} opts - Options that might contain a data property
 * @returns {Object} Normalized request config
 */
function extractDataFromOptions(opts = {}) {
  // If options has a data property, extract it
  if (opts && opts.data !== undefined) {
    const { data, ...restOpts } = opts
    const config = { data, ...restOpts }

    // remove Content-Type to let axios set multipart/form-data automatically
    if (data instanceof FormData) {
      config.headers = {
        ...config.headers,
        'Content-Type': undefined,
      }
    }

    return config
  }
  return opts
}

// Legacy export functions that adapt to the pattern used in your codebase
export function noAuthGet(url, opts = {}) {
  const config = extractDataFromOptions(opts)
  return noAuthService({ method: 'get', url, ...config })
}

export function noAuthPost(url, opts = {}) {
  const config = extractDataFromOptions(opts)
  return noAuthService({ method: 'post', url, ...config })
}

export function get(url, opts = {}) {
  const config = extractDataFromOptions(opts)
  return axiosService({ method: 'get', url, ...config })
}

export function galaxyGet(url, opts = {}) {
  const config = extractDataFromOptions(opts)
  return galaxyService({ method: 'get', url, ...config })
}

export function getBlobJson(url, opts = {}) {
  const config = extractDataFromOptions(opts)
  return axiosBlobJsonService({ method: 'get', url, ...config })
}

export function putBlobJson(url, opts = {}) {
  const config = extractDataFromOptions(opts)
  return axiosBlobJsonService({ method: 'put', url, ...config })
}

export function del(url, opts = {}) {
  const config = extractDataFromOptions(opts)
  return axiosService({ method: 'delete', url, ...config })
}

export function put(url, opts = {}) {
  const config = extractDataFromOptions(opts)
  return axiosService({ method: 'put', url, ...config })
}

export function patch(url, opts = {}) {
  const config = extractDataFromOptions(opts)
  return axiosService({ method: 'patch', url, ...config })
}

export function post(url, opts = {}) {
  const config = extractDataFromOptions(opts)
  return axiosService({ method: 'post', url, ...config })
}

export function cancelAllRequests(reason) {
  return http.requests.cancelAll(reason)
}

export default http
